import React from "react";
import { useLocation } from "react-router-dom";
import moment from "moment";

export const EventOptionLogos = ({
  logos = [],
  className = "",
  imgDir = "images/unga/logos",
  maxWidth = 162, // matches .ed-wrapper .header-bar .header-container .header-logos .header-logo
}) => {
  const httpLike = /^https?:\/\//i;

  const normalizeSrc = (src) => {
    if (!src) return "";
    if (httpLike.test(src)) return src.replace("betterblocks.dev", "betterblocks.landmarkventures.com"); // some pics have the old url
    const base = process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/` : "/";
    const hasExt = /\.[a-z0-9]+$/i.test(src);
    const file = hasExt ? src : `${src}.png`;
    return `${base}${imgDir.replace(/^\//, "")}/${file}`;
  };

  const scaleToWidth = (scale) => {
    const s = Number.isFinite(scale) ? scale : 100;
    const clamped = Math.min(300, Math.max(10, s)); // allow 10–300%
    return Math.round((clamped / 100) * maxWidth);
  };

  return (
    <>
      {logos.map((l, i) => {
        const x = Number.isFinite(l?.xOffset) ? l.xOffset : 0;
        const y = Number.isFinite(l?.yOffset) ? l.yOffset : 0;
        const style = {
          maxWidth: `${scaleToWidth(l?.scale)}px`,
          transform: (x || y) ? `translate(${x}px, ${y}px)` : undefined,
        };
        const imgSrc = normalizeSrc(l?.src || "");

        return (
          <div
            key={i}
            className={`logo-img ${className} ${i > 0 ? "mt" : ""}`}
            style={style}
          >
            <img className="img-fluid" src={imgSrc} alt={l?.alt || "logo"} />
          </div>
        );
      })}
    </>
  );
};

// Returns true if `email` looks like a personal/free-provider address
export const isPersonalEmail = (email) => {
  if (typeof email !== "string") return false;
  const at = email.lastIndexOf("@");
  if (at === -1) return false;

  const domain = email.slice(at + 1).trim().toLowerCase();

  // Common free/personal providers (extend as needed)
  const PERSONAL_DOMAIN_PATTERNS = [
    /^gmail\.com$/,
    /^googlemail\.com$/,
    /^yahoo\.(com|co\.[a-z]{2}|[a-z]{2})$/,
    /^(hotmail|live|outlook|msn)\.(com|co\.[a-z]{2}|[a-z]{2})$/,
    /^(icloud|mac|me)\.com$/,
    /^aol\.com$/,
    /^protonmail\.com$/,
    /^proton\.me$/,
    /^gmx\.(com|de)$/,
    /^mail\.com$/,
    /^yandex\.(ru|com)$/,
    /^zoho\.(com|eu)$/,        // Remove if you treat Zoho as business
    /^tutanota\.com$/,
    // UK ISP-style personal mailboxes
    /^(btinternet|virginmedia|ntlworld|talktalk|sky)\.(com|net|co\.uk)$/
  ];

  return PERSONAL_DOMAIN_PATTERNS.some(rx => rx.test(domain));
}

export const getDisplayDateFromStartEndDateString = (
  startDate,
  endDate,
  endDateEpoch = null
) => {
  // Check if startDate contains "TBD" or "TBA"
  if (startDate.includes("TBD") || startDate.includes("TBA")) {
    return startDate;
  }

  const startMoment = moment(startDate, "MMMM D");
  const endMoment = moment(endDate, "MMMM D");

  // Check if endDate is not valid
  if (!endMoment.isValid()) {
    return startDate;
  }

  const startMonth = startMoment.format("MMMM");
  const endMonth = endMoment.format("MMMM");

  // use epoch to determine year
  let yearPostfix = ``;
  if (endDateEpoch) {
    const date = new Date(endDateEpoch * 1000);
    const year = date.getFullYear();
    const multipleSpaces = "\u00A0\u00A0"; // Non-breaking space repeated multiple times
    yearPostfix = `${multipleSpaces}${year}`;
  }

  if (startMonth === endMonth) {
    // Same month
    const dateString = `${startMonth} ${startMoment.format(
      "D"
    )} - ${endMoment.format("D")},${yearPostfix}`;
    return dateString;
  } else {
    // Different months
    const dateString = `${startMonth} ${startMoment.format(
      "D"
    )} - ${endMonth} ${endMoment.format("D")},${yearPostfix}`;
    return dateString;
  }
};

export const epochToDate = (epochTime, options = { removeDay: false }) => {
  const date = new Date(epochTime * 1000); // convert seconds to milliseconds
  const month = date.toLocaleString("default", { month: "long" });
  const year = date.getFullYear();
  if (options.removeDay) {
    return `${month} ${year}`;
  }
  const day = date.getDate();
  // add the appropriate ordinal suffix to the day (e.g. "1st", "2nd", "3rd")
  let dayStr;
  if (day % 10 === 1 && day !== 11) {
    dayStr = day + "st";
  } else if (day % 10 === 2 && day !== 12) {
    dayStr = day + "nd";
  } else if (day % 10 === 3 && day !== 13) {
    dayStr = day + "rd";
  } else {
    dayStr = day + "th";
  }
  return `${dayStr} ${month} ${year}`;
};

export const dateToYYYYMMDD = (date) => {
  if (typeof date === "string") {
    date = new Date(date); // Convert string to Date object
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}/${month}/${day}`;
};

export const randomString = (length) => {
  let result = "";
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
};

export const useScrollToLocation = () => {
  const scrolledRef = React.useRef(false);
  const { hash } = useLocation();
  const hashRef = React.useRef(hash);

  React.useEffect(() => {
    if (hash) {
      // We want to reset if the hash has changed
      if (hashRef.current !== hash) {
        hashRef.current = hash;
        scrolledRef.current = false;
      }

      // only attempt to scroll if we haven't yet (this could have just reset above if hash changed)
      if (!scrolledRef.current) {
        const id = hash.replace("#", "");
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });
          // const elementTop =
          //   element.getBoundingClientRect().top + window.scrollY;
          // window.scrollTo({
          //   top: elementTop,
          //   behavior: "smooth",
          // });
          scrolledRef.current = true;
        }
      }
    }
  });
};

// Tuesday, January 10, 2024 --> Tuesday, January 10th
export const formatDatestring = (str, { shortDay = false }) => {
  try {
    let [day, date, year] = str.split(",");
    const dayOfMonth = date.trim().split(" ")[1];
    const postfix = getNumPostfix(dayOfMonth);
    if (shortDay) {
      const dayMap = {
        Sunday: "Sun",
        Monday: "Mon",
        Tuesday: "Tues",
        Wednesday: "Wed",
        Thursday: "Thurs",
        Friday: "Fri",
        Saturday: "Sat",
      };
      day = dayMap[day.trim()];
    }
    return `${day}, ${date}${postfix}`;
  } catch (error) {
    console.log(`formatDatestring() - error`, error);
    return str;
  }
};

export const getNumPostfix = (number) => {
  // Convert the number to a string to access its last digit
  const numStr = number.toString().trim();

  // Check for special cases: 11th, 12th, and 13th
  if (numStr.length >= 2 && numStr.slice(-2) === "11") {
    return "th";
  } else if (numStr.length >= 2 && numStr.slice(-2) === "12") {
    return "th";
  } else if (numStr.length >= 2 && numStr.slice(-2) === "13") {
    return "th";
  }

  // For other numbers, determine the postfix based on the last digit
  const lastDigit = numStr.slice(-1);

  switch (lastDigit) {
    case "1":
      return "st";
    case "2":
      return "nd";
    case "3":
      return "rd";
    default:
      return "th";
  }
};

export const setCookie = (name, value, days) => {
  // Calculate the expiration date
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);

  // Format the expiration date
  const expires = "expires=" + date.toUTCString();

  // Set the cookie
  document.cookie = name + "=" + value + ";" + expires + ";path=/";
};

export const getCookie = (name) => {
  // Create a regular expression to match the cookie name
  const nameEQ = name + "=";

  // Split document.cookie into individual cookies
  const cookies = document.cookie.split(";");

  // Loop through each cookie to find the one with the specified name
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];

    // Remove leading spaces
    while (cookie.charAt(0) === " ") {
      cookie = cookie.substring(1, cookie.length);
    }

    // If the cookie name matches, return its value
    if (cookie.indexOf(nameEQ) === 0) {
      return cookie.substring(nameEQ.length, cookie.length);
    }
  }

  // If the cookie with the specified name is not found, return null
  return null;
};

export const generateICSFile = (calendarOptions) => {
  const { title, description, startDateTime, endDateTime, location } =
    calendarOptions;

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  };

  const escapePlainText = (str) =>
    str
      .replace(/<br\s*\/?>/gi, "\\n")
      .replace(/<\/?[^>]+(>|$)/g, "") // strip HTML
      .replace(/\\/g, "\\\\")
      .replace(/,/g, "\\,")
      .replace(/;/g, "\\;")
      .replace(/\n/g, "\\n");

  const foldLine = (line) =>
    line.length <= 75 ? line : line.match(/.{1,73}/g).join("\r\n ");

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Landmark Ventures//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${Date.now()}@landmarkventures.com`,
    `DTSTAMP:${formatDate(new Date().toISOString())}`,
    `DTSTART:${formatDate(startDateTime)}`,
    `DTEND:${formatDate(endDateTime)}`,
    `SUMMARY:${title}`,
    `DESCRIPTION:${escapePlainText(description)}`,
    foldLine(`X-ALT-DESC;FMTTYPE=text/html:${description.replace(/\n/g, "")}`),
    `LOCATION:${location}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ];

  const icsContent = lines.join("\r\n");

  const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${title.replace(/\s+/g, "_")}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
